import { memo, type ComponentType, useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { useDraggable } from "@dnd-kit/core";
import { ICON_SIZE } from "@/utils/desktop-layout-utils";

type DesktopIconProps = {
  label: string;
  Icon: ComponentType<{ className?: string; style?: React.CSSProperties }>;
  onActivate?: () => void;
  showIcon?: boolean;
  showLabel?: boolean;
  iconColor?: string;
  textColor?: string;
  wiggleTrigger?: number;
  appId: string;
  position?: { x: number; y: number };
};

function DesktopIcon({
  label,
  Icon,
  onActivate,
  showIcon = true,
  showLabel = true,
  iconColor = "#F5A623",
  textColor = "#FFFFFF",
  wiggleTrigger = 0,
  appId,
  position = { x: 0, y: 0 },
}: DesktopIconProps) {
  const iconControls = useAnimation();
  const [isHovered, setIsHovered] = useState(false);
  const [allowHover, setAllowHover] = useState(true);
  // Track ob die Position schon einmal gesetzt wurde (fuer Snap-Transition nach Drag)
  const hasSettledRef = useRef(false);

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `desktop-icon-${appId}`,
      data: {
        type: "icon",
        id: appId,
        source: "desktop",
        size: "icon",
      },
    });

  // Apply dnd-kit transform only when dragging
  // Otherwise use the position passed from parent
  const x = isDragging && transform ? position.x + transform.x : position.x;
  const y = isDragging && transform ? position.y + transform.y : position.y;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate?.();
    }
  };

  useEffect(() => {
    iconControls.set({ rotate: 0 });
  }, [iconControls]);

  useEffect(() => {
    if (!wiggleTrigger) return;
    iconControls.start({
      rotate: [0, -3, 3, -3, 0],
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    });
  }, [wiggleTrigger, iconControls]);

  // Handle snapping behavior: disable hover shortly after drop
  useEffect(() => {
    if (!isDragging) {
      setAllowHover(false);
      const timer = setTimeout(() => setAllowHover(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isDragging]);

  // Nach dem ersten Render Snap-Transition aktivieren
  useEffect(() => {
    if (!hasSettledRef.current) {
      // requestAnimationFrame damit der erste Render ohne Transition stattfindet
      const id = requestAnimationFrame(() => {
        hasSettledRef.current = true;
      });
      return () => cancelAnimationFrame(id);
    }
  }, []);

  // CSS-basierte Positionierung statt Framer Motion animate={{ x, y }}
  // Beim Initial-Load keine Transition (hasSettled=false), danach Spring-aehnliche Transition fuer Snap
  const useTransition = hasSettledRef.current && !isDragging;

  return (
    <div
      ref={setNodeRef}
      className="group select-none flex flex-col items-center justify-start rounded-lg"
      style={{
        width: `${ICON_SIZE.width}px`,
        minHeight: `${ICON_SIZE.height}px`,
        paddingTop: "10px",
        position: "absolute",
        left: 0,
        top: 0,
        cursor: isDragging ? "grabbing" : "pointer",
        zIndex: isDragging ? 1000 : 1,
        touchAction: "none",
        transform: `translate3d(${x}px, ${y}px, 0) scale(${isHovered && !isDragging && allowHover ? 1.05 : 1})`,
        backgroundColor:
          isHovered && !isDragging && allowHover
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(255, 255, 255, 0)",
        transition: useTransition
          ? "transform 0.25s cubic-bezier(0.25, 1, 0.5, 1), background-color 0.15s ease"
          : "background-color 0.15s ease",
        willChange: isDragging ? "transform" : "auto",
      }}
      {...attributes}
      {...listeners}
      role="button"
      aria-label={label}
      tabIndex={0}
      onClick={() => {
        if (!isDragging) {
          onActivate?.();
        }
      }}
      onKeyDown={onKeyDown}
      onMouseEnter={() => {
        if (!isDragging && allowHover) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      data-driver-id="desktop-icon"
      data-driver-app-id={appId}
    >
      {showIcon && (
        <motion.div
          animate={iconControls}
          onHoverStart={() => {
            iconControls.start({
              rotate: [0, -3, 3, -3, 0],
              transition: {
                duration: 0.5,
                ease: "easeInOut",
              },
            });
          }}
        >
          <Icon
            className="h-10 w-10 transition-colors duration-150"
            style={{ color: iconColor }}
          />
        </motion.div>
      )}
      {showLabel && (
        <div
          className="text-center w-full px-1 break-words"
          style={{
            marginTop: showIcon ? "4px" : "0px",
            textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
            fontSize: "11px",
            color: textColor,
            fontWeight: "500",
            lineHeight: "1.2",
            wordBreak: "break-word",
          }}
          title={label}
        >
          {label}
        </div>
      )}
    </div>
  );
}

export default memo(DesktopIcon);
